import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CartLineT, LineItemType } from '@lambda/apis/src/cart/types';
import type { OfferT } from '@lambda/apis/src/catalog-v2/types';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { ShippingSKU } from '@lambda/apis/src/shipping/types';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { DeliveryEstimateT } from '@lambda/apis/src/catalog-v2/types';
import { CartWarrantyT } from '@/lib/reebelocare/types';
import { getAccessoriesPath, isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import { priceFormater } from '@/settings/index';
import { priceFormatSuperscript } from '@/lib/price';
import SavingsBadge from '../common/SavingsBadge';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isInternalUser } from '@/lib/internal-user';
import {
  trackAddToCart,
  trackRemoveFromCart,
} from '@/lib/analytics/trackProduct';
import { ProductSource } from '@/lib/analytics/types';
import DeliveryToggle, { FreeDeliveryBadge } from '../common/DeliveryToggle';
import QuantitySelector from '../common/QuantitySelector';
import { getMaxQuantity } from '../common/quantity';
import useAvailableQuantity, {
  fetchOfferAllocation,
} from '@/lib/use-offer-allocation';
import { allocateQuantity } from '../common/offerAllocation';
import OfferSplitModal from '../OfferSplitModal';
import FrequentlyBoughtTogether from './FrequentlyBoughtTogether';
import LineItemReebeloCare from './LineItemReebeloCare';
import {
  averageUnitPrice,
  buildSiblingOfferNotes,
  combineDeliveryRange,
  collectLinesToRemove,
  formatDeliveryDates,
  getCartLineProductLink,
  getCartLineSkuId,
  isAccessoryLine,
} from '../helpers';
import SellerBreakdown from '../common/SellerBreakdown';
import { getCartDiscountSubtotal } from '@/lib/order-discounts/helpers';
import i18n from '@/settings/i18n';
import { splitProductTitle } from '../../product/sections/main/right/product/helpers';
import { formatDeliveryRange } from '../../product/hooks/helpers';

const t = i18n.init('cart');

// `gap-2` between Remove and Qty, `gap-3` between them and the price.
const ACTIONS_GAP_PX = 8;
const PRICE_GAP_PX = 12;

/**
 * Whether the price and its actions (Remove, Qty) overflow their row side by
 * side: then the actions go to a line of their own below. Measured from their
 * own widths, which moving them doesn't change, so it can't flip back and
 * forth.
 */
const useWrapsWhenNarrow = (enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [wrapped, setWrapped] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const lead = leadRef.current;
    const actions = actionsRef.current;

    if (
      !enabled ||
      !container ||
      !lead ||
      !actions ||
      typeof ResizeObserver === 'undefined'
    ) {
      return undefined;
    }

    const check = () => {
      const items = Array.from(actions.children) as HTMLElement[];
      const needed =
        lead.offsetWidth +
        PRICE_GAP_PX +
        items.reduce((width, item) => width + item.offsetWidth, 0) +
        ACTIONS_GAP_PX * (items.length - 1);

      setWrapped(needed > container.clientWidth);
    };
    const observer = new ResizeObserver(check);

    observer.observe(container);
    observer.observe(lead);
    observer.observe(actions);

    return () => observer.disconnect();
  }, [enabled]);

  return { containerRef, leadRef, actionsRef, wrapped };
};

const LineRow = ({
  lines,
  expressShippingOffer,
  deliveryEstimates,
  quantityControl,
  onRemove,
}: {
  lines: CartLineT[];
  expressShippingOffer?: ShippingSKU;
  deliveryEstimates: Record<string, DeliveryEstimateT[]>;
  quantityControl: ReactNode;
  onRemove: () => void;
}) => {
  const [firstLine] = lines;
  const isMultiSeller = lines.length > 1;
  const { notes = [], title } = firstLine;

  const { primaryTitle, secondaryTitle } = splitProductTitle(title);
  const { customer } = useCustomerContext();
  const isPurchaseFlow = isPurchaseFlowEnabled(customer?.email);
  const priceRow = useWrapsWhenNarrow(isPurchaseFlow);

  const comparePrice = notes.find(
    (note) => note.name === '_compare_price',
  )?.value;

  // Sellers charge different unit prices, so the line shows what a unit
  // averages out at across them rather than any one seller's figure.
  const price = isMultiSeller ? averageUnitPrice(lines) : firstLine.price;

  const comparedTo = comparePrice ? Number(comparePrice) : null;
  const savings = comparedTo ? comparedTo - price / 100 : null;

  const productLink = getCartLineProductLink(firstLine) ?? '#';

  // Several sellers means several parcels, so the window runs from the first
  // arrival to the last — and express is off the table, since only one of them
  // could honour it.
  const combinedRange = combineDeliveryRange(
    lines.map((line) => deliveryEstimates[line.id]),
  );
  // The purchase flow writes it as a single seller's delivery reads
  // ("FREE Delivery Oct 28 - 30").
  const combinedShortRange =
    combinedRange &&
    formatDeliveryRange(
      combinedRange.minDeliveryDate,
      combinedRange.maxDeliveryDate,
      { format: 'short', locale: i18n.getLocale() },
    );

  const removeButton = (
    <button
      className="text-xs text-[#99A1AF] transition duration-150 ease-in-out hover:text-gray-700 hover:underline"
      onClick={onRemove}
    >
      {t`Remove`}
    </button>
  );

  return (
    <div className="p-4">
      {/* The purchase flow's desktop is a grid: the image beside two rows,
          title and variants opposite the price, then delivery opposite
          Remove + Qty. Its wrappers step aside (`lg:contents`) so their
          children are the grid's cells. */}
      <div
        className={classNames(
          'flex flex-col justify-between gap-4',
          isPurchaseFlow
            ? 'lg:grid lg:grid-cols-[90px_minmax(0,1fr)_auto] lg:gap-x-4 lg:gap-y-3'
            : 'lg:flex-row lg:gap-4',
        )}
      >
        <div
          className={classNames(
            'flex flex-1 gap-2 md:gap-4',
            // The purchase flow's image stays at the top beside the title and
            // delivery.
            isPurchaseFlow ? 'items-start lg:contents' : 'items-center',
          )}
        >
          <Link
            href={productLink}
            className={classNames(
              'h-[70px] w-[70px] shrink-0 transition duration-150 ease-in-out hover:scale-105 xs:h-[80px] xs:w-[80px] lg:h-[90px] lg:w-[90px]',
              isPurchaseFlow &&
                (isMultiSeller ? 'lg:row-span-3' : 'lg:row-span-2'),
            )}
            target="_blank"
          >
            <Image
              src={firstLine?.image ?? ''}
              alt={firstLine?.title ?? ''}
              width={96}
              height={96}
              className="h-full w-full object-contain"
            />
          </Link>
          <div
            className={classNames(
              'flex h-full w-full flex-col gap-1 md:gap-4 lg:justify-between',
              isPurchaseFlow && 'lg:contents',
            )}
          >
            <Link
              href={productLink}
              className={classNames(
                'group',
                isPurchaseFlow && 'lg:col-start-2',
              )}
              target="_blank"
            >
              <h2
                className={classNames(
                  'group-hover:underline',
                  isPurchaseFlow
                    ? 'text-sm font-medium leading-tight lg:text-[15px]'
                    : 'text-sm font-bold lg:text-base',
                )}
              >
                {primaryTitle}
              </h2>
              {secondaryTitle && (
                <p
                  className={classNames(
                    'text-xs text-gray-700/75 lg:text-sm',
                    isPurchaseFlow ? 'mt-0.5' : 'mt-1 lg:mt-0',
                  )}
                >
                  {secondaryTitle}
                </p>
              )}
            </Link>
            <div
              className={classNames(
                'flex flex-col gap-2',
                // Desktop's delivery is in the row below; a seller note gets
                // a row of its own under the title and price.
                isPurchaseFlow &&
                  (isMultiSeller
                    ? 'lg:col-span-2 lg:col-start-2'
                    : 'lg:hidden'),
                // The seller note sits 6px from the variants above and the
                // delivery below: the title column's 4px gap (16px on
                // tablet) and the desktop grid's 12px, brought to 6px.
                isPurchaseFlow &&
                  isMultiSeller &&
                  'mt-0.5 gap-1.5 md:-mt-2.5 lg:-mt-1.5',
              )}
            >
              {isMultiSeller ? (
                <>
                  {isPurchaseFlow ? (
                    // Under the seller note on mobile and tablet; desktop's
                    // is in the row below.
                    <FreeDeliveryBadge
                      date={combinedShortRange}
                      className="order-last lg:hidden"
                    />
                  ) : (
                    <div className="hidden h-8 w-fit items-center rounded-full border border-[#EAEAEA] bg-[#F8F8F8] px-2.5 text-xs font-medium md:flex">
                      {t`Free Delivery`}{' '}
                      {combinedRange && formatDeliveryDates(combinedRange)}
                    </div>
                  )}
                  <SellerBreakdown
                    lines={lines}
                    deliveryEstimates={deliveryEstimates}
                  />
                </>
              ) : (
                <DeliveryToggle
                  line={firstLine}
                  // Desktop's is in the full-width row below.
                  className={isPurchaseFlow ? 'mt-2 md:mt-0' : 'hidden md:flex'}
                  expressShippingOffer={expressShippingOffer}
                  deliveryEstimates={deliveryEstimates[firstLine.id]}
                  pills={isPurchaseFlow}
                />
              )}
            </div>
          </div>
        </div>
        {isMultiSeller
          ? !isPurchaseFlow && (
              <div className="flex h-8 w-fit items-center rounded-full border border-[#EAEAEA] bg-[#F8F8F8] px-2.5 text-xs font-medium md:hidden">
                {t`Free Delivery`}{' '}
                {combinedRange && formatDeliveryDates(combinedRange)}
              </div>
            )
          : !isPurchaseFlow && (
              <DeliveryToggle
                line={firstLine}
                className="md:hidden"
                expressShippingOffer={expressShippingOffer}
                deliveryEstimates={deliveryEstimates[firstLine.id]}
              />
            )}
        {/* In the purchase flow, mobile keeps it to one row when it fits: the
            price and its saving opposite Remove and Qty, without the
            struck-through price. Otherwise Remove and Qty go below, on the
            right. */}
        <div
          ref={isPurchaseFlow ? priceRow.containerRef : undefined}
          className={classNames(
            'flex justify-between lg:flex-col lg:items-end lg:text-right',
            isPurchaseFlow
              ? [
                  'items-start lg:col-start-3 lg:row-start-1',
                  priceRow.wrapped ? 'flex-col gap-2 lg:gap-3' : 'gap-3',
                ]
              : 'flex-col items-start gap-2 xs:flex-row xs:items-end xs:gap-6',
          )}
        >
          <div
            ref={isPurchaseFlow ? priceRow.leadRef : undefined}
            className={classNames(
              'flex lg:flex-col lg:items-end',
              isPurchaseFlow
                ? 'h-8 shrink-0 items-center gap-2 lg:h-auto lg:gap-1'
                : 'w-full flex-wrap items-start justify-between gap-x-2 gap-y-1 xxs:flex-row xs:flex-col',
            )}
          >
            <div
              className="flex items-center text-base font-bold lg:text-lg"
              translate="no"
            >
              {priceFormatSuperscript(priceFormater.long(price / 100))}
            </div>
            <SavingsBadge
              savings={savings}
              comparePrice={comparedTo}
              comparePriceClassName={
                isPurchaseFlow ? 'hidden lg:inline' : undefined
              }
            />
          </div>
          {isPurchaseFlow ? (
            <div
              ref={priceRow.actionsRef}
              className={classNames(
                'flex items-center gap-2 lg:hidden',
                priceRow.wrapped ? 'self-end' : 'shrink-0',
              )}
            >
              {removeButton}
              {quantityControl}
            </div>
          ) : (
            <div className="flex shrink-0 items-center justify-end gap-2 self-end">
              {removeButton}
              {quantityControl}
            </div>
          )}
        </div>
        {isPurchaseFlow && (
          // Desktop's second row: the delivery, Remove + Qty at the card's
          // right edge below the price. Without room, Remove + Qty go below on
          // the right. The price row has them on mobile.
          <div
            className={classNames(
              'hidden flex-wrap items-center justify-between gap-x-4 gap-y-2 lg:col-span-2 lg:col-start-2 lg:flex',
              // Close under a seller note: 6px, not the grid's 12px.
              isMultiSeller && 'lg:-mt-1.5',
            )}
          >
            {isMultiSeller ? (
              <FreeDeliveryBadge date={combinedShortRange} />
            ) : (
              <DeliveryToggle
                line={firstLine}
                expressShippingOffer={expressShippingOffer}
                deliveryEstimates={deliveryEstimates[firstLine.id]}
                pills
              />
            )}
            {/* `ml-auto`: at the right end, on the delivery's line or below. */}
            <div className="ml-auto flex items-center gap-2">
              {removeButton}
              {quantityControl}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LineItem = ({
  lines,
  expressShippingOffers,
  deliveryEstimates,
  reebeloCareSubtotal,
  warranties,
  categoryWarranties,
}: {
  lines: CartLineT[];
  expressShippingOffers: Record<string, ShippingSKU>;
  deliveryEstimates: Record<string, DeliveryEstimateT[]>;
  reebeloCareSubtotal: number;
  warranties: CartWarrantyT;
  categoryWarranties: Record<string, ProductCategoryWarranty>;
}) => {
  const [firstLine] = lines;

  const {
    addLinesToCart,
    removeLinesFromCart,
    updateCart,
    updateCartLines,
    cartLoading,
    cartLines,
  } = useCartContext();

  // Set when a requested quantity would span sellers, which needs confirming.
  const [splitPrompt, setSplitPrompt] = useState<{
    offers: OfferT[];
    quantity: number;
  } | null>(null);

  // Everything in the cart bar this SKU. The split modal adds whatever the
  // shopper is picking to it, so the discount badge tracks the change live.
  const otherLinesSubtotal = getCartDiscountSubtotal(
    cartLines.filter((line) => !lines.some((l) => l.id === line.id)),
    reebeloCareSubtotal,
  );

  // The whole group is one SKU, so it carries one quantity — the sum of what
  // each seller's line holds.
  const groupQuantity = lines.reduce((total, line) => total + line.quantity, 0);
  const fallbackStock = lines.reduce(
    (total, line) => total + (line.stock ?? 0),
    0,
  );

  // MsCatalog2 knows what can actually be allocated across this SKU's offers
  // right now; the lines' own `stock` is only the fallback while that is in
  // flight. It caps both the dropdown and the typed-quantity field.
  const skuId = firstLine.notes?.find((note) => note.name === '_sku_id')?.value;
  // Splitting a SKU across sellers is still an internal trial. Everyone else
  // keeps the plain behaviour: one cart line, capped by its own stock, updated
  // in place — and no allocation request on their behalf either.
  const { customer } = useCustomerContext();
  const splitEnabled = isInternalUser(customer?.email);
  const isPurchaseFlow = isPurchaseFlowEnabled(customer?.email);
  const lineSkuId = getCartLineSkuId(firstLine);
  const accessoriesPath = lineSkuId ? getAccessoriesPath(lineSkuId) : null;

  // One request per SKU for the ceiling; the split asks separately, because
  // which offers get drawn on depends on the quantity requested.
  const availableQuantity = useAvailableQuantity(
    splitEnabled ? skuId : undefined,
  );

  const maxQuantity = getMaxQuantity(
    splitEnabled ? availableQuantity ?? fallbackStock : fallbackStock,
    firstLine.psku,
  );

  /**
   * Writes a split to the cart: each seller's line is set to its share, shares
   * with no line yet become one, and lines the split no longer needs go.
   */
  const commitSplit = async (offers: OfferT[], updatedQuantity: number) => {
    const split = allocateQuantity(offers, updatedQuantity);

    // One request each for the lines to change and the lines to add.
    await updateCartLines(
      split.flatMap((share) => {
        const existing = lines.find((l) => l.typeId === share.offerId);

        return existing && existing.quantity !== share.quantity
          ? [{ id: existing.id, quantity: share.quantity }]
          : [];
      }),
    );

    await addLinesToCart(
      split.flatMap((share) => {
        const existing = lines.find((l) => l.typeId === share.offerId);
        const offer = offers.find((o) => o.id === share.offerId);

        return !existing && offer
          ? [
              {
                typeId: share.offerId,
                quantity: share.quantity,
                notes: buildSiblingOfferNotes(firstLine, offer),
                type: LineItemType.PRODUCT,
              },
            ]
          : [];
      }),
    );

    // Lowering the quantity can leave a seller with nothing to fulfil; one
    // control for the group means those lines have to go.
    const dropped = lines.filter(
      (l) => !split.some((share) => share.offerId === l.typeId),
    );

    await removeLinesFromCart(dropped);
  };

  /**
   * A vendor only holds so much stock. A quantity one offer can cover is
   * applied straight away; one that would be spread over several sellers means
   * several deliveries at several unit prices, so it is put to the shopper
   * first.
   */
  const applyQuantity = async (updatedQuantity: number) => {
    if (!splitEnabled) {
      await updateCart(firstLine.id, { quantity: updatedQuantity });

      return;
    }

    // Always ask for the ceiling, never just what was requested: the modal
    // lists every seller holding this SKU, so a request for 6 still has to
    // show the seller with 2 left. The split is worked out locally from there.
    const allocation = skuId
      ? await fetchOfferAllocation(skuId, availableQuantity ?? updatedQuantity)
      : null;

    const offers = allocation?.items ?? [];
    const split = allocateQuantity(offers, updatedQuantity);

    // Nothing to split on (no sku note, or the call failed) — behave as before.
    if (split.length === 0) {
      await updateCart(firstLine.id, { quantity: updatedQuantity });

      return;
    }

    if (split.length === 1) {
      await commitSplit(offers, updatedQuantity);

      return;
    }

    setSplitPrompt({ offers, quantity: updatedQuantity });
  };

  // On Quantity Change
  const onQuantityChange = async (updatedQuantity: number) => {
    if (updatedQuantity > groupQuantity) {
      trackAddToCart({
        cartLine: firstLine,
        properties: {
          quantity: updatedQuantity,
          source: ProductSource.CART_SUMMARY,
        },
      });
    } else {
      trackRemoveFromCart({
        cartLine: firstLine,
        properties: {
          quantity: updatedQuantity,
          source: ProductSource.CART_SUMMARY,
        },
      });
    }

    await applyQuantity(updatedQuantity);
  };

  // Removing the SKU takes every seller's line with it, and any recommendation
  // hanging off one of them, in a single request.
  // Accessories are their own line items in the purchase flow, so they stay
  // when the device goes; otherwise they're removed with it.
  const onRemoveGroup = async () => {
    await removeLinesFromCart(
      isPurchaseFlow ? lines : collectLinesToRemove(lines, cartLines),
    );
  };

  return (
    <div
      className={classNames(
        'rounded-2xl border shadow-[0_16px_5px_0_rgba(0,_0,_0,_0.00),_0_10px_4px_0_rgba(0,_0,_0,_0.00),_0_6px_4px_0_rgba(0,_0,_0,_0.01),_0_3px_3px_0_rgba(0,_0,_0,_0.02),_0_1px_1px_0_rgba(0,_0,_0,_0.02)]',
        // The purchase flow's page is grey on mobile too, so the card goes
        // straight on it, as on desktop.
        isPurchaseFlow ? 'bg-white' : 'bg-[#F8F8F8] p-4 lg:bg-white lg:p-0',
      )}
    >
      <div className="rounded-2xl bg-white">
        <LineRow
          lines={lines}
          expressShippingOffer={expressShippingOffers[firstLine.id]}
          deliveryEstimates={deliveryEstimates}
          onRemove={onRemoveGroup}
          quantityControl={
            <QuantitySelector
              quantity={groupQuantity}
              max={maxQuantity}
              disabled={cartLoading}
              onChange={onQuantityChange}
            />
          }
        />
      </div>
      {splitPrompt && (
        <OfferSplitModal
          showModal
          setShowModal={() => setSplitPrompt(null)}
          line={firstLine}
          otherLinesSubtotal={otherLinesSubtotal}
          offers={splitPrompt.offers}
          quantity={splitPrompt.quantity}
          loading={cartLoading}
          onConfirm={async (confirmed) => {
            await commitSplit(splitPrompt.offers, confirmed);
            setSplitPrompt(null);
          }}
        />
      )}
      {/* In the purchase flow the device's ReebeloCare takes this slot, and
          its accessories have their own page; otherwise Frequently Bought
          Together. */}
      {!isAccessoryLine(firstLine) &&
        (isPurchaseFlow ? (
          <div className="rounded-b-2xl bg-white">
            <LineItemReebeloCare
              lines={lines}
              plansByLine={Object.fromEntries(
                lines
                  .filter((line) => warranties[line.id])
                  .map((line) => [
                    line.id,
                    warranties[line.id].productWarranty,
                  ]),
              )}
              categoryWarranty={
                firstLine.category?.pk
                  ? categoryWarranties[firstLine.category.pk]
                  : undefined
              }
            />
            {accessoriesPath && (
              <div className="border-t border-[#F2F2F2] px-4 py-3 text-xs font-medium">
                <Link
                  href={accessoriesPath}
                  className="inline-flex items-center gap-1.5 hover:text-gray-700/80"
                >
                  <span aria-hidden>+</span>
                  <span className="underline">
                    {t`Add accessories for your ${{
                      product: splitProductTitle(firstLine.title).primaryTitle,
                    }}`}
                  </span>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <FrequentlyBoughtTogether
            lineId={firstLine.id}
            psku={firstLine.psku}
          />
        ))}
    </div>
  );
};

export default LineItem;
